import type { SyntheticEvent } from 'react';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as tokens from '@tokens';

import { baseStyles, ghostStyles } from '@components/Button/styles';
import { Icon } from '@components/Icon/Icon';
import type { IconName } from '@components/Icon/BaseIcon';
import { useToggleGroup } from '@components/ToggleGroup/toggle-group-context';
import { Tooltip } from '@components/Tooltip/Tooltip';

export interface ToggleProps {
  /**
   * Text content of the toggle.
   */
  children: string;
  /**
   * Value of the toggle.
   */
  value: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Icon to be shown in the button. Will always show on the left.
   */
  icon?: IconName;
  /**
   * Indicates if toggle is rendered as disabled.
   */
  isDisabled?: boolean;
  /**
   * Text content of a tooltip appearing when the toggle is disabled.
   */
  disabledTooltipText?: string;
  /**
   * Callback when the a key is pressed.
   */
  onKeyDown?: (e: SyntheticEvent<HTMLButtonElement>) => void;
  /**
   * Callback when the toggle is pressed.
   */
  onClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
}

const ButtonWrapper = styled('span')`
  display: inline-flex;
`;

const StyledButton = styled('button')`
  ${baseStyles};
  ${ghostStyles};
  ${tokens.elevation(0)};
  height: ${tokens.spacer.x4};
  padding: 0 ${tokens.spacer.x1pt5};
  font-weight: 400;

  &:hover {
    background-color: ${tokens.BRAND_COLORS.neutral_300};
  }

  &[aria-checked='true'] {
    background: ${tokens.color.ui_background};
    ${tokens.elevation(1)};
  }

  &:focus {
    ${tokens.focus()};
  }

  &[disabled] {
    cursor: not-allowed;
    background-color: ${tokens.color.ui_disabled};
    ${tokens.elevation(0)};
    color: ${tokens.color.text_disabled};
  }
`;

/**
 * Toggle component.
 *
 * {@link https://zeroheight.com/76c439891/p/57c441-button-toggle-group Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-togglegroup Code documentation}
 *
 * @example
 * <ToggleGroup.Toggle value="option A" onClick={}>Action Text</ToggleGroup.Toggle>
 */
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    { children, icon, isDisabled, disabledTooltipText, ...props }: ToggleProps,
    ref,
  ) => {
    const { selectedValue } = useToggleGroup();

    const isSelected = Boolean(selectedValue) && selectedValue === props.value;

    const button = (
      <StyledButton
        {...props}
        disabled={isDisabled}
        aria-checked={isSelected}
        ref={ref}
        role="radio"
      >
        {icon && (
          <Icon
            icon={icon}
            size="m"
            css={css`
              margin-right: ${tokens.spacer.x1};
            `}
          />
        )}
        {children}
      </StyledButton>
    );

    if (isDisabled && disabledTooltipText) {
      return (
        <Tooltip text={disabledTooltipText} placement={'top'}>
          <ButtonWrapper>{button}</ButtonWrapper>
        </Tooltip>
      );
    }

    // Button is wrapped around <></>
    // to avoid a strange "missing in props validation " lint error
    return <>{button}</>;
  },
);

Toggle.displayName = 'Toggle';

Toggle.defaultProps = {
  isDisabled: false,
};
